import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/*
 * Warping Theories changes to quick add (search "WT:"):
 * - The product card's hover/focus pre-download and the click share one download per product, and its content is
 *   kept, so a click after a hover opens straight away. A press on the button starts the download too (no hover on
 *   touch screens).
 * - After the first product page, only the product section is downloaded (about a third of the size, same content).
 * - The button shows a spinner until the popup opens; clicks while it loads are ignored; if two cards are clicked
 *   quickly, only the last one opens.
 * - If the product can't be loaded, the click opens the product page instead of doing nothing.
 */

/** WT: the section that holds the product content, learned from the first product page of the visit. */
const SECTION_ID_KEY = `quick-add-section:${window.Shopify?.theme?.id ?? ''}`;

/** WT: the latest quick add click; an older click that finishes later doesn't replace its product. */
let latestQuickAdd = 0;

/** @returns {string | null} */
function readQuickAddSectionId() {
  try {
    return sessionStorage.getItem(SECTION_ID_KEY);
  } catch {
    return null;
  }
}

/** @param {string} sectionId */
function rememberQuickAddSectionId(sectionId) {
  if (!sectionId) return;
  try {
    sessionStorage.setItem(SECTION_ID_KEY, sectionId);
  } catch {
    // Without storage every popup downloads the whole page, as before.
  }
}

/**
 * Downloads a product page, or one section of it, and returns a copy of its product content with the id of the
 * section it sits in.
 * @param {string} url
 * @returns {Promise<{ grid: Element, sectionId: string } | null>}
 */
async function fetchProductGrid(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const grid = html.querySelector('[data-product-grid-content]');
  if (!grid) return null;
  const sectionId = grid.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') ?? '';
  return { grid: /** @type {Element} */ (grid.cloneNode(true)), sectionId };
}

/**
 * WT: product content for the popup, shared by every quick add on the page. A card holds one quick add per colour
 * option, and its hover pre-download can go to a different one than the button that gets clicked.
 * @type {Map<string, Element>}
 */
const cachedContent = new Map();
/** @type {Map<string, Promise<Element | null>>} WT: downloads in progress, shared by hover, press and click. */
const loading = new Map();
/** WT: goes up when the cart changes, so a download that started before the change isn't kept. */
let cacheGeneration = 0;

/**
 * WT: the product's content for the popup, from the cache, from a download already running, or downloaded now.
 * Once the product section's id is known, only that section is downloaded; if that fails (say the theme changed),
 * the whole page is, and its section id is remembered for next time.
 * @param {string} productUrl - The product page URL
 * @returns {Promise<Element | null>}
 */
function loadProductGrid(productUrl) {
  if (!productUrl) return Promise.resolve(null);
  const url = new URL(productUrl, window.location.href).toString();
  const cached = cachedContent.get(url);
  if (cached) return Promise.resolve(cached);
  const running = loading.get(url);
  if (running) return running;

  const generation = cacheGeneration;
  const download = (async () => {
    let result = null;
    const sectionId = readQuickAddSectionId();
    if (sectionId) {
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', sectionId);
      result = await fetchProductGrid(sectionUrl.toString()).catch(() => null);
    }
    if (!result) {
      result = await fetchProductGrid(url);
      if (result) rememberQuickAddSectionId(result.sectionId);
    }
    if (!result) return null;
    if (generation === cacheGeneration) cachedContent.set(url, result.grid);
    return result.grid;
  })();

  loading.set(url, download);
  download.then(
    () => loading.delete(url),
    () => loading.delete(url)
  );
  return download;
}

export class QuickAddComponent extends Component {
  /** WT: true while a click is loading its product. */
  #opening = false;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    // WT: right after the page loads, quick add can be ready before the product card's own script; read the card's
    // link straight from the page then, instead of failing on the missing method.
    const productLink =
      (typeof productCard?.getProductCardLink === 'function'
        ? productCard.getProductCardLink()
        : /** @type {HTMLAnchorElement | null | undefined} */ (productCard?.querySelector('a[ref="productCardLink"]'))) ||
      hotspotProduct?.getHotspotProductLink?.();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (typeof productCard?.getSelectedVariantId === 'function') return productCard.getSelectedVariantId() || null;
    // WT: the card's script may not have loaded yet (see productPageUrl): read the checked option from the page.
    const checked = productCard?.querySelector('input[type="radio"]:checked[data-variant-id]');
    return checked instanceof HTMLInputElement ? checked.dataset.variantId || null : null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.addEventListener('pointerdown', this.#handlePointerDown, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
  }

  /**
   * WT: a press on the "choose" button starts the download, a moment before the click arrives.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.quick-add__button--choose')) return;
    this.fetchProductPage(this.productPageUrl);
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    cachedContent.clear();
    cacheGeneration += 1;
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // WT: a second click while the product loads would only start over.
    if (this.#opening) return;
    this.#opening = true;
    const ticket = ++latestQuickAdd;

    // WT: spinner on the button until the popup opens (styles in assets/base.css, [data-quick-add-loading]).
    // Product hotspots call this without a real click, so fall back to the component's own button.
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.quick-add__button') ?? this.querySelector('.quick-add__button--choose');
    button?.setAttribute('data-quick-add-loading', '');

    // WT: everything that can fail sits inside try, so the button is always released.
    let currentUrl = '';
    try {
      currentUrl = this.productPageUrl;
      const productGrid = await loadProductGrid(currentUrl);
      if (!productGrid) throw new Error('No product content on the product page');
      // WT: a later quick add click took over.
      if (ticket !== latestQuickAdd) return;

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      // WT: the product couldn't be loaded here, so open its page; the click still leads somewhere.
      console.warn('[quick-add] Opening the product page instead:', error);
      if (currentUrl && ticket === latestQuickAdd) window.location.assign(currentUrl);
    } finally {
      button?.removeAttribute('data-quick-add-loading');
      this.#opening = false;
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * WT: starts loading a product for the popup ahead of the click and keeps it. Product cards call this on hover and
   * focus, and after a variant change. Errors are left to the click, which tries again.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   */
  async fetchProductPage(productPageUrl) {
    try {
      await loadProductGrid(productPageUrl);
    } catch {
      // The click downloads again and falls back to the product page.
    }
  }

  /**
   * WT: the popup's photos don't need to be bigger than the card's. Any photo the card has already loaded (usually
   * the first one and the hover one) is shown from that same file, so it appears at once; the other photos are sized
   * like the card's photo, so nothing larger is downloaded.
   * @param {Element} productGrid - The product content about to go into the popup
   */
  #useCardImages(productGrid) {
    const card = this.closest('product-card');
    if (!card) return;
    /** @param {string} src */
    const fileOf = (src) => {
      try {
        return new URL(src, window.location.href).pathname;
      } catch {
        return '';
      }
    };

    /** @type {Map<string, string>} the card's loaded photos: file path -> the exact address it loaded */
    const loaded = new Map();
    let cardWidth = 0;
    for (const img of card.querySelectorAll('img')) {
      if (!(img instanceof HTMLImageElement) || !img.complete || !img.naturalWidth || !img.currentSrc) continue;
      loaded.set(fileOf(img.currentSrc), img.currentSrc);
      cardWidth = Math.max(cardWidth, Math.ceil(img.getBoundingClientRect().width));
    }
    if (!loaded.size) return;

    for (const img of productGrid.querySelectorAll('.product-media img')) {
      if (!(img instanceof HTMLImageElement)) continue;
      const cached = loaded.get(fileOf(img.getAttribute('src') || ''));
      if (cached) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('loading');
        img.src = cached;
      } else if (cardWidth) {
        img.setAttribute('sizes', `${cardWidth}px`);
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#useCardImages(productGrid);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
